import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Formik, FormikProps } from 'formik';
import { observer } from 'mobx-react';
import { mdiChevronRight } from '@mdi/js';
import { ActionMeta, MultiValue } from 'react-select';
import { ClaimEditorStore, DISABLED_PRIMARY_TPA_PAYER_MESSAGE, DISABLED_SECONDARY_TPA_PAYER_MESSAGE, DISABLED_TERTIARY_TPA_PAYER_MESSAGE } from '../../../../stores/claim-editor';
import { ProviderPayer } from '../../../../api';
import { BaseInput } from '../../../base/input';
import { BaseSelect, ISelectOption } from '../../../base/select';
import { DatePicker } from '../../../base/datepicker';
import { BaseButton } from '../../../base/button';
import { BaseFileUploader } from '../../../base/file-uploader';
import * as S from './settings-form.styles';
import * as FormS from '../form.styles';
import * as SectionS from '../section.styles';
import { FormErrorBanner } from '../../../base/form/form-error-banner';

interface SettingsFormValues {
    maximumGuarantorResponsibility: { amount: string; currency: string };
    primaryThirdPartyAdministratorPayerId: string;
    secondaryThirdPartyAdministratorPayerId: string;
    tertiaryThirdPartyAdministratorPayerId: string;
}

interface ISettingsFormProps {
    claimEditorStore: ClaimEditorStore;
}

export const SettingsForm: React.FunctionComponent<ISettingsFormProps> = observer(({ claimEditorStore }) => {
    const formikRef = useRef<FormikProps<SettingsFormValues> | null>(null);
    // Use cached payers from store (loaded at claim-editor level)
    const payers = (claimEditorStore as any)['payersCache'] || [];
    const isLoadingPayers = !(claimEditorStore as any)['payersLoaded'];
    const [prePaymentsOpen, setPrePaymentsOpen] = useState(() => {
        return (claimEditorStore?.guarantorPayments?.length || 0) > 0;
    });
    const [sellerOpen, setSellerOpen] = useState(() => {
        const seller = claimEditorStore?.seller;
        return !!(seller?.externalId || seller?.first || seller?.last || seller?.organization);
    });
    const [documentsOpen, setDocumentsOpen] = useState(false);
    const [prePayments, setPrePayments] = useState<Array<{ date?: string; amount?: string; memo?: string }>>([]);
    const [seller, setSeller] = useState({ sellerId: undefined as string | undefined, externalId: '', first: '', last: '', organization: '' });
    const [uploadedFiles, setUploadedFiles] = useState<Array<{ id: string; name: string }>>([]);
    const [isUploadingDocuments, setIsUploadingDocuments] = useState(false);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const initialValues = useMemo<SettingsFormValues>(() => {
        const settings = claimEditorStore?.settings;
        console.log('Settings:', settings);
        return {
            maximumGuarantorResponsibility: {
                amount: settings?.maximumGuarantorResponsibility?.amount || '',
                currency: settings?.maximumGuarantorResponsibility?.currency || 'USD'
            },
            primaryThirdPartyAdministratorPayerId: settings?.primaryThirdPartyAdministratorPayerId || '',
            secondaryThirdPartyAdministratorPayerId: settings?.secondaryThirdPartyAdministratorPayerId || '',
            tertiaryThirdPartyAdministratorPayerId: settings?.tertiaryThirdPartyAdministratorPayerId || '',
        };
    }, [claimEditorStore?.settings]);


    // Re-initialize pre-payments from the store whenever the editor points at a different
    // session ("Create Another" or a real claim switch) — not just on mount — so a previous
    // claim's payments do not linger in this mount-only local state.
    useEffect(() => {
        const payments = claimEditorStore?.guarantorPayments || [];
        setPrePayments(payments.map(p => ({
            date: p.collectionDate || '',
            amount: p.amount?.amount || '',
            memo: p.memo || '',
        })));
    }, [claimEditorStore?.formSessionId]);

    // Re-initialize seller from the store on the same session boundary. Reset unconditionally
    // (not only `if (sellerData)`) so a session with no seller clears out a previous one.
    useEffect(() => {
        const sellerData = claimEditorStore?.seller;
        setSeller({
            sellerId: sellerData?.sellerId,
            externalId: sellerData?.externalId || '',
            first: sellerData?.first || '',
            last: sellerData?.last || '',
            organization: sellerData?.organization || '',
        });
    }, [claimEditorStore?.formSessionId]);

    // Re-initialize uploaded files (ids only, names not available) on the same session boundary.
    // Reset unconditionally so a session with no documents clears out a previous one.
    useEffect(() => {
        const ids = claimEditorStore?.documents || [];
        setUploadedFiles(ids.map(id => ({ id, name: 'Document' })));
    }, [claimEditorStore?.formSessionId]);

    const handleAddPrePayment = () => {
        setPrePayments(prev => [...prev, { date: '', amount: '', memo: '' }]);
    };

    const handleRemovePrePayment = (index: number) => {
        setPrePayments(prev => {
            const updated = prev.filter((_, i) => i !== index);
            claimEditorStore.updateguarantorPayments(
                updated.map(p => ({ 
                    collectionDate: p.date, 
                    amount: { amount: p.amount || '0', currency: 'USD' }, 
                    memo: p.memo 
                }))
            );
            return updated;
        });
    };

    const handlePrePaymentChange = (index: number, field: string, value: string) => {
        setPrePayments(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            claimEditorStore.updateguarantorPayments(
                updated.map(p => ({ 
                    collectionDate: p.date, 
                    amount: { amount: p.amount || '0', currency: 'USD' }, 
                    memo: p.memo 
                }))
            );
            return updated;
        });
        console.log('Updated guarantor payments in store:', claimEditorStore.guarantorPayments);
    };

    const handleSellerChange = (field: string, value: string) => {
        setSeller(prev => {
            const updated = { ...prev, [field]: value };
            claimEditorStore.updateSeller(updated);
            return updated;
        });
    };

    const handleFilesSelected = async (files: FileList | null) => {
        if (!files || files.length === 0) return;
        setIsUploadingDocuments(true);
        const next: Array<{ id: string; name: string }> = [];
        for (const file of Array.from(files)) {
            try {
                const documentId = await claimEditorStore.uploadDocument(file);
                if (documentId) {
                    next.push({ id: documentId, name: file.name });
                }
            } catch (err) {
                console.error('Error uploading document:', err);
            }
        }
        if (next.length > 0) {
            setUploadedFiles(prev => [...prev, ...next]);
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        setIsUploadingDocuments(false);
    };

    const removeUploadedFile = (id: string) => {
        setUploadedFiles(prev => prev.filter(f => f.id !== id));
        const remaining = (claimEditorStore.documents || []).filter(d => d !== id);
        claimEditorStore.updateDocuments(remaining);
    };

    const openFilePicker = () => {
        if (fileInputRef.current) fileInputRef.current.click();
    };

    const payerOptions: ISelectOption[] = [
        ...payers.map(p => ({
            value: p.id || '',
            label: p.name || 'Unnamed Payer',
        })),
    ];

    return (
        <Formik<SettingsFormValues>
            innerRef={formikRef as any}
            initialValues={initialValues}
            onSubmit={() => {}}
            enableReinitialize
            validateOnMount={false}
            validateOnChange={true}
            validateOnBlur={false}
        >
            {formik => (
                <FormS.FormContainer>
                    <FormErrorBanner
                        messages={[
                            ...(!claimEditorStore.isPayerIdActive(formik.values.primaryThirdPartyAdministratorPayerId)
                                ? [DISABLED_PRIMARY_TPA_PAYER_MESSAGE]
                                : []),
                            ...(!claimEditorStore.isPayerIdActive(formik.values.secondaryThirdPartyAdministratorPayerId)
                                ? [DISABLED_SECONDARY_TPA_PAYER_MESSAGE]
                                : []),
                            ...(!claimEditorStore.isPayerIdActive(formik.values.tertiaryThirdPartyAdministratorPayerId)
                                ? [DISABLED_TERTIARY_TPA_PAYER_MESSAGE]
                                : []),
                        ]}
                    />
                    {/* General Settings Section - Always Visible */}
                    <div>
                        <FormS.FormGrid>
                            <FormS.FormField>
                                <FormS.Label>Maximum Guarantor Responsibility</FormS.Label>
                                <S.CurrencyInputWrapper>
                                    <S.CurrencyPrefix>US$</S.CurrencyPrefix>
                                    <BaseInput
                                        name="maximumGuarantorResponsibility"
                                        type="number"
                                        step="0.01"
                                        placeholder="0.00"
                                        value={formik.values.maximumGuarantorResponsibility?.amount}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            const amountValue = e.target.value;
                                            formik.setFieldValue('maximumGuarantorResponsibility', {
                                                amount: amountValue,
                                                currency: 'USD'
                                            });
                                            claimEditorStore.updateSettings({
                                                ...claimEditorStore.settings,
                                                maximumGuarantorResponsibility: amountValue ? {
                                                    amount: amountValue,
                                                    currency: 'USD'
                                                } : undefined,
                                            });
                                        }}
                                        style={{ paddingLeft: '38px' }}
                                    />
                                </S.CurrencyInputWrapper>
                            </FormS.FormField>

                            <FormS.FormField>
                                <FormS.Label>Primary Third Party Administrator</FormS.Label>
                                <S.ConstrainedSelect>
                                    <BaseSelect
                                        options={payerOptions}
                                        value={payerOptions.find(opt => opt.value === formik.values.primaryThirdPartyAdministratorPayerId)}
                                        onChange={(
                                            newValue: ISelectOption | MultiValue<ISelectOption> | null,
                                            actionMeta: ActionMeta<ISelectOption>
                                        ) => {
                                            const option = newValue as ISelectOption | null;
                                            const value = option?.value as string || '';
                                            formik.setFieldValue('primaryThirdPartyAdministratorPayerId', value);
                                            claimEditorStore.updateSettings({
                                                ...claimEditorStore.settings,
                                                primaryThirdPartyAdministratorPayerId: value,
                                            });
                                        }}
                                        isDisabled={isLoadingPayers}
                                        placeholder="Select Primary Third Party Administrator"
                                    />
                                </S.ConstrainedSelect>
                            </FormS.FormField>

                            <FormS.FormField>
                                <FormS.Label>Secondary Third Party Administrator</FormS.Label>
                                <S.ConstrainedSelect>
                                    <BaseSelect
                                        options={payerOptions}
                                        value={payerOptions.find(opt => opt.value === formik.values.secondaryThirdPartyAdministratorPayerId)}
                                        onChange={(
                                            newValue: ISelectOption | MultiValue<ISelectOption> | null,
                                            actionMeta: ActionMeta<ISelectOption>
                                        ) => {
                                            const option = newValue as ISelectOption | null;
                                            const value = option?.value as string || '';
                                            formik.setFieldValue('secondaryThirdPartyAdministratorPayerId', value);
                                            claimEditorStore.updateSettings({
                                                ...claimEditorStore.settings,
                                                secondaryThirdPartyAdministratorPayerId: value,
                                            });
                                        }}
                                        isDisabled={isLoadingPayers}
                                        placeholder="Select Secondary Third Party Administrator"
                                    />
                                </S.ConstrainedSelect>
                            </FormS.FormField>

                            <FormS.FormField>
                                <FormS.Label>Tertiary Third Party Administrator</FormS.Label>
                                <S.ConstrainedSelect>
                                    <BaseSelect
                                        options={payerOptions}
                                        value={payerOptions.find(opt => opt.value === formik.values.tertiaryThirdPartyAdministratorPayerId)}
                                        onChange={(
                                            newValue: ISelectOption | MultiValue<ISelectOption> | null,
                                            actionMeta: ActionMeta<ISelectOption>
                                        ) => {
                                            const option = newValue as ISelectOption | null;
                                            const value = option?.value as string || '';
                                            formik.setFieldValue('tertiaryThirdPartyAdministratorPayerId', value);
                                            claimEditorStore.updateSettings({
                                                ...claimEditorStore.settings,
                                                tertiaryThirdPartyAdministratorPayerId: value,
                                            });
                                        }}
                                        isDisabled={isLoadingPayers}
                                        placeholder="Select Tertiary Third Party Administrator"
                                    />
                                </S.ConstrainedSelect>
                            </FormS.FormField>
                        </FormS.FormGrid>
                    </div>

                    {/* Guarantor Pre-Payments Section - Collapsible */}
                    <div style={{ marginTop: '24px' }}>
                        <S.CollapsibleHeader onClick={() => setPrePaymentsOpen(!prePaymentsOpen)}>
                            <S.CollapsibleTitle>
                                <S.ExpandIcon path={mdiChevronRight} size={1} open={prePaymentsOpen} />
                                Guarantor Pre-Payments
                            </S.CollapsibleTitle>
                        </S.CollapsibleHeader>
                        <S.CollapsibleContent open={prePaymentsOpen}>
                            {prePayments.map((payment, idx) => {
                                const dateValue = payment.date ? payment.date.split('T')[0] : '';
                                
                                return (
                                    <SectionS.SectionContainer key={idx}>
                                        <SectionS.SectionHeader>
                                            <SectionS.SectionTitle>Payment {idx + 1}</SectionS.SectionTitle>
                                            <BaseButton onClick={() => handleRemovePrePayment(idx)} variant="warning">
                                                ✕ Delete Guarantor Payment
                                            </BaseButton>
                                        </SectionS.SectionHeader>
                                        <FormS.FormGrid>
                                            <FormS.FormField>
                                                <FormS.Label required>Payment Date</FormS.Label>
                                                <DatePicker
                                                    value={dateValue}
                                                    onDateSelected={(date: Date | undefined) => {
                                                        const dateStr = date && date instanceof Date && !isNaN(date.getTime()) 
                                                            ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
                                                            : '';
                                                        handlePrePaymentChange(idx, 'date', dateStr);
                                                    }}
                                                    inputProps={{
                                                        placeholder: 'MMM D, YYYY'
                                                    }}
                                                />
                                            </FormS.FormField>
                                        <FormS.FormField>
                                            <FormS.Label required>Payment Amount</FormS.Label>
                                            <S.CurrencyInputWrapper>
                                                <S.CurrencyPrefix>US$</S.CurrencyPrefix>
                                                <BaseInput
                                                    type="number"
                                                    step="0.01"
                                                    placeholder="0.00"
                                                    value={payment.amount}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                                                        handlePrePaymentChange(idx, 'amount', e.target.value)
                                                    }
                                                    style={{ paddingLeft: '38px' }}
                                                />
                                            </S.CurrencyInputWrapper>
                                        </FormS.FormField>
                                        <FormS.FormField span={2}>
                                            <FormS.Label>Payment Memo</FormS.Label>
                                            <BaseInput
                                                placeholder="Enter value"
                                                value={payment.memo}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                                                    handlePrePaymentChange(idx, 'memo', e.target.value)
                                                }
                                            />
                                        </FormS.FormField>
                                    </FormS.FormGrid>
                                </SectionS.SectionContainer>
                                );
                            })}
                            <SectionS.ButtonContainer>
                                <BaseButton onClick={handleAddPrePayment} variant="primary">
                                    + Add Guarantor Payment
                                </BaseButton>
                            </SectionS.ButtonContainer>
                        </S.CollapsibleContent>
                    </div>

                    {/* Seller Section - Collapsible */}
                    <div style={{ marginTop: '24px' }}>
                        <S.CollapsibleHeader onClick={() => setSellerOpen(!sellerOpen)}>
                            <S.CollapsibleTitle>
                                <S.ExpandIcon path={mdiChevronRight} size={1} open={sellerOpen} />
                                Seller
                            </S.CollapsibleTitle>
                        </S.CollapsibleHeader>
                        <S.CollapsibleContent open={sellerOpen}>
                            <FormS.FormGrid>
                                <FormS.FormField>
                                    <FormS.Label required>External System Id</FormS.Label>
                                    <BaseInput
                                        placeholder="External System Id"
                                        value={seller.externalId}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                                            handleSellerChange('externalId', e.target.value)
                                        }
                                    />
                                </FormS.FormField>
                                <FormS.FormField>
                                    <FormS.Label>First Name</FormS.Label>
                                    <BaseInput
                                        placeholder="First Name"
                                        value={seller.first}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                                            handleSellerChange('first', e.target.value)
                                        }
                                    />
                                </FormS.FormField>
                                <FormS.FormField>
                                    <FormS.Label>Last Name</FormS.Label>
                                    <BaseInput
                                        placeholder="Last Name"
                                        value={seller.last}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                                            handleSellerChange('last', e.target.value)
                                        }
                                    />
                                </FormS.FormField>
                                <FormS.FormField>
                                    <FormS.Label>Organization</FormS.Label>
                                    <BaseInput
                                        placeholder="Organization"
                                        value={seller.organization}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                                            handleSellerChange('organization', e.target.value)
                                        }
                                    />
                                </FormS.FormField>
                            </FormS.FormGrid>
                        </S.CollapsibleContent>
                    </div>

                    {/* New Documents Section - Collapsible */}
                    <div style={{ marginTop: '24px' }}>
                        <S.CollapsibleHeader onClick={() => setDocumentsOpen(!documentsOpen)}>
                            <S.CollapsibleTitle>
                                <S.ExpandIcon path={mdiChevronRight} size={1} open={documentsOpen} />
                                New Documents
                            </S.CollapsibleTitle>
                        </S.CollapsibleHeader>
                        <S.CollapsibleContent open={documentsOpen}>
                            <div>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    onChange={(e) => handleFilesSelected((e.target as HTMLInputElement).files)}
                                    style={{ display: 'none' }}
                                />
                                <BaseButton variant="primary" onClick={openFilePicker} disabled={isUploadingDocuments}>
                                    {isUploadingDocuments ? 'Uploading…' : '+ Add Documents'}
                                </BaseButton>
                                {uploadedFiles.length > 0 && (
                                    <div style={{ marginTop: '12px' }}>
                                        {uploadedFiles.map(f => (
                                            <div key={f.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '6px 0', borderTop: '1px solid #eee' }}>
                                                <span>{f.name}</span>
                                                <BaseButton variant="warning" onClick={() => removeUploadedFile(f.id)}>✕ Remove</BaseButton>
                                            </div>
                                        ))}
                                        <S.DropzoneSubtext style={{ marginTop: '8px' }}>
                                            These files will be attached to the claim.
                                        </S.DropzoneSubtext>
                                    </div>
                                )}
                            </div>
                        </S.CollapsibleContent>
                    </div>
                </FormS.FormContainer>
            )}
        </Formik>
    );
});

export default SettingsForm;

